import React, { useState, useEffect, useRef } from 'react'
import { motion, useSpring } from 'framer-motion'

const AnimatedCursorSVG = ({ isHovering, isOverProjectCard }) => {
  // State 3: Project card cursor with glass effect rectangle
  if (isOverProjectCard) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }}
        className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg pointer-events-none"
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
        }}
      >
        <span className="text-white text-sm font-medium whitespace-nowrap">view work</span>
      </motion.div>
    )
  }

  // State 1 & 2: Default circle states
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={40} height={40} viewBox="0 0 40 40" fill="none">
      {/* Inner circle that animates to fill the outer circle */}
      <motion.circle
        cx="20"
        cy="20"
        fill="white"
        initial={{ r: 6 }}
        animate={{ r: isHovering ? 19 : 6 }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
      />
    </svg>
  )
}

export default function SmoothCursor({
  springConfig = {
    damping: 40,
    stiffness: 600,
    mass: 1,
    restDelta: 0.001,
  },
}) {
  // Don't render on mobile devices
  const [isMobile, setIsMobile] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [isOverInteractive, setIsOverInteractive] = useState(false)
  const [isOverProjectCard, setIsOverProjectCard] = useState(false)
  const lastMousePos = useRef({ x: 0, y: 0 })
  const velocity = useRef({ x: 0, y: 0 })
  const lastUpdateTime = useRef(Date.now())
  const previousAngle = useRef(0)
  const accumulatedRotation = useRef(0)
  const isOverProjectCardRef = useRef(false)

  const cursorX = useSpring(0, springConfig)
  const cursorY = useSpring(0, springConfig)

  // Trailing circle with slower movement
  const trailX = useSpring(0, {
    ...springConfig,
    damping: 50,
    stiffness: 350,
  })
  const trailY = useSpring(0, {
    ...springConfig,
    damping: 50,
    stiffness: 350,
  })

  const rotation = useSpring(0, {
    ...springConfig,
    damping: 50,
    stiffness: 450,
  })
  const scale = useSpring(1, {
    ...springConfig,
    stiffness: 700,
    damping: 30,
  })

  useEffect(() => {
    const updateVelocity = (currentPos) => {
      const currentTime = Date.now()
      const deltaTime = currentTime - lastUpdateTime.current

      if (deltaTime > 0) {
        velocity.current = {
          x: (currentPos.x - lastMousePos.current.x) / deltaTime,
          y: (currentPos.y - lastMousePos.current.y) / deltaTime,
        }
      }

      lastUpdateTime.current = currentTime
      lastMousePos.current = currentPos
    }

    const smoothMouseMove = (e) => {
      const currentPos = { x: e.clientX, y: e.clientY }
      updateVelocity(currentPos)

      const speed = Math.sqrt(Math.pow(velocity.current.x, 2) + Math.pow(velocity.current.y, 2))

      cursorX.set(currentPos.x)
      cursorY.set(currentPos.y)

      // Update trailing circle with delay
      trailX.set(currentPos.x)
      trailY.set(currentPos.y)

      if (speed > 0.1 && !isOverProjectCardRef.current) {
        const currentAngle =
          Math.atan2(velocity.current.y, velocity.current.x) * (180 / Math.PI) + 90

        let angleDiff = currentAngle - previousAngle.current
        if (angleDiff > 180) angleDiff -= 360
        if (angleDiff < -180) angleDiff += 360
        accumulatedRotation.current += angleDiff
        rotation.set(accumulatedRotation.current)
        previousAngle.current = currentAngle

        scale.set(0.95)
        setIsMoving(true)

        const timeout = setTimeout(() => {
          scale.set(1)
          setIsMoving(false)
        }, 150)

        return () => clearTimeout(timeout)
      }
    }

    let rafId
    const throttledMouseMove = (e) => {
      if (rafId) return

      rafId = requestAnimationFrame(() => {
        smoothMouseMove(e)
        rafId = 0
      })
    }

    const handleMouseOver = (e) => {
      const target = e.target

      // Check if hovering over project card
      const isProjectCard =
        target.closest('.project-card') ||
        target.closest('[data-cursor="project-card"]') ||
        target.closest('.group.cursor-pointer') ||
        target.classList.contains('project-card')

      const isInteractive =
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.getAttribute('role') === 'button' ||
        target.getAttribute('tabindex') !== null ||
        target.classList.contains('cursor-pointer') ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('[role="button"]')

      setIsOverProjectCard(!!isProjectCard)
      isOverProjectCardRef.current = !!isProjectCard
      setIsOverInteractive(!!isInteractive && !isProjectCard)
    }

    window.addEventListener('mousemove', throttledMouseMove)
    document.addEventListener('mouseover', handleMouseOver)

    return () => {
      window.removeEventListener('mousemove', throttledMouseMove)
      document.removeEventListener('mouseover', handleMouseOver)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [cursorX, cursorY, trailX, trailY, rotation, scale])

  // Check for mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Don't render anything on mobile
  if (isMobile) {
    return null
  }

  return (
    <>
      {/* Trailing circle */}
      <motion.div
        style={{
          position: 'fixed',
          left: trailX,
          top: trailY,
          translateX: '-50%',
          translateY: '-50%',
          zIndex: 99,
          pointerEvents: 'none',
          willChange: 'transform',
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={40}
          height={40}
          viewBox="0 0 40 40"
          fill="none"
        >
          <circle
            cx="20"
            cy="20"
            r="19"
            fill="none"
            stroke="white"
            strokeWidth="2"
            opacity={isOverInteractive || isOverProjectCard ? 0 : 0.3}
            style={{ transition: 'opacity 0.1s ease-out' }}
          />
        </svg>
      </motion.div>

      {/* Main cursor dot */}
      <motion.div
        style={{
          position: 'fixed',
          left: cursorX,
          top: cursorY,
          translateX: '-50%',
          translateY: '-50%',
          rotate: isOverProjectCard ? 0 : rotation,
          scale: isOverProjectCard ? 1 : scale,
          zIndex: 100,
          pointerEvents: 'none',
          willChange: 'transform',
          mixBlendMode: isOverInteractive ? 'difference' : 'normal',
          cursor: isOverProjectCard ? 'pointer' : 'auto',
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
      >
        <AnimatedCursorSVG isHovering={isOverInteractive} isOverProjectCard={isOverProjectCard} />
      </motion.div>
    </>
  )
}
